import React from 'react';

import {
  Button,
  Heading,
  Icon,
  IconButton,
  SegmentedControl,
  Skeleton,
  Text
} from '@albert-io/atomic';
import {ListRowsSkeleton, SkeletonRow} from 'client/TeacherDashboard/components/ListRowsSkeleton';

import {TAB_SEGMENTS} from './constants';

// Rows-only placeholder for the in-list loading state (search / filter refetch), where the heading
// and controls already stay mounted — so a search shows skeleton rows, not a collapsing spinner.
export const AssignmentRowsSkeleton = () => <ListRowsSkeleton label='Loading assignments…' />;

// The list reveals only once counts have loaded, so the real segments always carry a total. The
// placeholder digits keep the masked control the same width as the one that replaces it.
const SEGMENTS_WITH_COUNTS = TAB_SEGMENTS.map((s) => ({
  ...s,
  label: (
    <>
      {s.label} <span className='ml-x0-half content-tertiary'>(00)</span>
    </>
  )
}));

/**
 * First-paint placeholder for the assignments view, matching its real layout (heading, status
 * segments, controls, rows) so tabs, counts, and rows reveal in place with no shift, no
 * "Status (0)" flash, and no tabs-before-empty-state flash (#19132).
 */
export const AssignmentsViewSkeleton = () => (
  <div aria-busy='true' className='gap-x3 pt-x4 flex flex-col'>
    <span className='sr-only' role='status'>
      Loading assignments…
    </span>
    <Heading as='h2' className='m-x0' size='s'>
      Assignments
    </Heading>
    <div aria-hidden='true' className='gap-x2 flex flex-wrap items-center justify-between'>
      <Skeleton>
        <SegmentedControl
          buttonClassName='font-normal'
          options={SEGMENTS_WITH_COUNTS}
          selectedIdx={0}
        />
      </Skeleton>
      <div className='gap-x1 flex items-center'>
        <Skeleton>
          <Button color='secondary' variant='outlined'>
            <Icon className='mr-x1' icon='bars-filter' />
            <Text color='inherit'>Filters</Text>
          </Button>
        </Skeleton>
        <Skeleton>
          <Button color='secondary' variant='outlined'>
            <Icon className='mr-x1' icon={['far', 'arrow-down-short-wide']} />
            <Text color='inherit'>Sort</Text>
          </Button>
        </Skeleton>
        <Skeleton>
          <IconButton
            aria-hidden
            color='secondary'
            icon='magnifying-glass'
            roundness='square'
            variant='outlined'
          />
        </Skeleton>
        <Skeleton>
          <Button>
            <Icon className='mr-x1' icon='file-plus' iconStyle='regular' />
            New
          </Button>
        </Skeleton>
      </div>
    </div>
    <div aria-hidden='true' className='flex flex-col'>
      {['r1', 'r2', 'r3', 'r4', 'r5', 'r6'].map((key) => (
        <SkeletonRow key={key} />
      ))}
    </div>
  </div>
);
